import React, { createContext, useContext, useState, useEffect } from 'react';
import { API_BASE } from '../utils';

const DEFAULT_BRANDING = {
  brandId: null,
  brandName: 'CallVibe',
  logoUrl: '/Logo.png',
  faviconUrl: null, // null = leave the default favicon tags in public/index.html alone
};

const BrandingContext = createContext({ ...DEFAULT_BRANDING, brandLoading: false });

export const useBranding = () => useContext(BrandingContext);

const cacheKeyFor = (hostname) => `callvibe_branding:${hostname}`;

const readCachedBranding = (hostname) => {
  try {
    const raw = localStorage.getItem(cacheKeyFor(hostname));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null; // localStorage unavailable (private mode, quota, etc.) — non-fatal
  }
};

export const BrandingProvider = ({ children }) => {
  const hostname = window.location.hostname;
  // Show last-known branding instantly while the fetch below verifies it in the background.
  const cached = readCachedBranding(hostname);
  const [branding, setBranding] = useState(cached || DEFAULT_BRANDING);
  // True until we know whether this hostname is branded (see App.js).
  const [brandLoading, setBrandLoading] = useState(!cached);

  useEffect(() => {
    let cancelled = false;

    fetch(`${API_BASE}/public/branding`, {
      headers: { 'X-Tenant-Hostname': hostname },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled) return;
        const resolved =
          data && data.brand_id
            ? {
                brandId: data.brand_id,
                brandName: data.brand_name || DEFAULT_BRANDING.brandName,
                logoUrl: data.logo_url || DEFAULT_BRANDING.logoUrl,
                faviconUrl: data.favicon_url || null,
              }
            : DEFAULT_BRANDING;
        setBranding(resolved);
        try {
          localStorage.setItem(cacheKeyFor(hostname), JSON.stringify(resolved));
        } catch {
          // non-fatal — just means we won't have a cache for next time
        }
      })
      .catch(() => {
        // Keep whatever we already have (cached or default) if the lookup fails
      })
      .finally(() => {
        if (!cancelled) setBrandLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [hostname]);

  // Tab title/icon live outside React's DOM, so update them imperatively.
  useEffect(() => {
    document.title = branding.brandName;
    if (branding.faviconUrl) {
      document.querySelectorAll("link[rel~='icon']").forEach((link) => {
        link.href = branding.faviconUrl;
      });
    }
  }, [branding]);

  return (
    <BrandingContext.Provider value={{ ...branding, brandLoading }}>
      {children}
    </BrandingContext.Provider>
  );
};
